import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  type AuthController,
  type DataSource,
  type Entity,
  type User,
  useAuthController,
  useDataSource,
  useSnackbarController,
} from "@firecms/core";
import type { AuthExtras } from "../types/auth";
import { canManageEvent } from "../firebase/membershipQueries";
import { getEventManagerAccessDeniedMessage } from "./eventAccessMessages";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import type { Event } from "../collections/events";
import {
  isEventProvisioningReady,
  provisioningDenialMessage,
} from "../collections/eventProvisioning";
import type { Space } from "../collections/spaces";
import { resolveLocalizedString } from "../localization";
import {
  modeAccountId,
  modeEventId,
  modeSpaceId,
  resolveAdminMode,
  getWorkspaceKey,
  type AdminMode,
} from "./adminMode";
import { buildCMSViewsForMode } from "./buildCMSViews";
import {
  buildAccountPath,
  buildEventPath,
  buildSpacePath,
  workspaceParamsFromRoute,
  type WorkspaceRouteParams,
} from "./workspaceRoutes";
import { eventEntityTitle } from "../actions/ManageEventButton";
import { toEventCollectionIdFromEntity } from "./eventIdentity";
import useStickyState from "../hooks/useStickyState";

function applyHydratedSpace(mode: AdminMode, spaceId: string): AdminMode {
  if (mode.kind !== "event" || mode.id.spaceId || !spaceId) {
    return mode;
  }
  return { kind: "event", id: { ...mode.id, spaceId } };
}

export function getAppBarTitle(workspace: {
  mode: AdminMode;
  title: string;
  accountTitle: string;
  spaceTitle: string;
}): string {
  switch (workspace.mode.kind) {
    case "hub":
      return "TeamOps Admin";
    case "space":
      return (
        workspace.spaceTitle ||
        workspace.accountTitle ||
        workspace.title ||
        "Learning Path"
      );
    case "account":
      return workspace.accountTitle || workspace.title || "TeamOps Admin";
    case "event":
      return workspace.title || workspace.accountTitle || "Event";
    default:
      return "TeamOps Admin";
  }
}

function isInvitePathname(pathname: string): boolean {
  return pathname === "/invite";
}

function isHubPathname(pathname: string): boolean {
  return pathname === "/" || isInvitePathname(pathname);
}

export function useAdminWorkspace() {
  const routeParams = useParams<WorkspaceRouteParams>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const snackbarController = useSnackbarController();
  const authController = useAuthController();
  const dataSource = useDataSource();

  const routeMode = useMemo(() => {
    if (isHubPathname(pathname)) {
      return { kind: "hub" } as const;
    }
    const params = workspaceParamsFromRoute(routeParams);
    return resolveAdminMode(params);
  }, [pathname, routeParams.accountId, routeParams.spaceId, routeParams.eventId]);

  const [hydratedSpaceId, setHydratedSpaceId] = useStickyState(
    "",
    "hydratedEventSpaceId"
  );

  const mode = useMemo(
    () => applyHydratedSpace(routeMode, hydratedSpaceId),
    [routeMode, hydratedSpaceId]
  );

  const selectedAccountId = modeAccountId(mode) ?? "";
  const selectedSpaceId = modeSpaceId(mode) ?? "";
  const selectedEventId = modeEventId(mode) ?? "";
  const workspaceKey = getWorkspaceKey(mode);

  const [title, setTitle] = useStickyState("", "workspaceTitle");
  const [accountTitle, setAccountTitle] = useStickyState(
    "",
    selectedAccountId ? `accountTitle:${selectedAccountId}` : "accountTitle"
  );
  const [spaceTitle, setSpaceTitle] = useStickyState(
    "",
    selectedAccountId && selectedSpaceId
      ? `spaceTitle:${selectedAccountId}:${selectedSpaceId}`
      : "spaceTitle"
  );

  const clearToAccount = useCallback(() => {
    if (!selectedAccountId) {
      return;
    }
    setSpaceTitle("");
    setHydratedSpaceId("");
    setTitle(accountTitle);
    navigate(buildAccountPath(selectedAccountId));
  }, [
    accountTitle,
    navigate,
    selectedAccountId,
    setHydratedSpaceId,
    setSpaceTitle,
    setTitle,
  ]);

  const openSpace = useCallback(
    (entity: Entity<Space>, accountId: string) => {
      setSpaceTitle(
        resolveLocalizedString(entity.values.title) || entity.values.name
      );
      navigate(buildSpacePath({ accountId, spaceId: entity.id }));
    },
    [navigate, setSpaceTitle]
  );

  const openEvent = useCallback(
    async (entity: Entity<Event>) => {
      if (!isEventProvisioningReady(entity.values.status)) {
        snackbarController.open({
          type: "info",
          message: provisioningDenialMessage(
            {
              status: entity.values.status,
              provisioning: entity.values.provisioning,
            },
            getAdminUiLocale()
          ),
        });
        return;
      }

      const id = toEventCollectionIdFromEntity(entity);
      if (!id) {
        return;
      }

      const extra = authController.extra as AuthExtras | undefined;
      const userId = authController.user?.uid;
      if (!extra?.isSuperManager && userId) {
        const allowed = await canManageEvent(
          dataSource,
          userId,
          id.accountId,
          id.eventId
        );
        if (!allowed) {
          snackbarController.open({
            type: "info",
            message: getEventManagerAccessDeniedMessage(getAdminUiLocale()),
          });
          return;
        }
      }

      setTitle(eventEntityTitle(entity));
      if (id.spaceId) {
        setHydratedSpaceId(id.spaceId);
      }
      navigate(buildEventPath(id));
    },
    [
      authController.extra,
      authController.user?.uid,
      dataSource,
      navigate,
      setHydratedSpaceId,
      setTitle,
      snackbarController,
    ]
  );

  const buildCMSViews = useCallback(
    async (_: {
      user: User | null;
      authController: AuthController;
      dataSource: DataSource;
    }) => buildCMSViewsForMode(mode),
    [mode]
  );

  return {
    mode,
    isInvite: isInvitePathname(pathname),
    workspaceKey,
    selectedAccountId,
    selectedSpaceId,
    selectedEventId,
    title,
    setTitle,
    accountTitle,
    setAccountTitle,
    spaceTitle,
    setSpaceTitle,
    hydratedSpaceId,
    setHydratedSpaceId,
    clearToAccount,
    openSpace,
    openEvent,
    buildCMSViews,
  };
}
